<template>
  <div>
    <Message v-if="isClosed" type="is-warning">
      <template v-slot:content>
        {{ $t('Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Text_Closed') }}
      </template>
    </Message>

    <template v-else>
      <p class="is-size-7 has-text-grey mb-3">
        {{
          $t('Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Text_OpenBetween', {
            start: $options.filters.minutesToTime(daySchedule.MinMinutes),
            end: $options.filters.minutesToTime(daySchedule.MaxMinutes),
          })
        }}
      </p>

      <Message v-if="!hasAvailableSlots" type="is-warning">
        <template v-slot:content>
          {{ $t('Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Text_NoSlots') }}
        </template>
      </Message>

      <div v-else class="field">
        <label class="label">{{
          $t('Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Label_Time')
        }}</label>
        <p class="is-size-7 has-text-grey mb-2">
          {{ $t('Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Text_DragHint') }}
        </p>
        <div class="slot-grid" @touchmove.prevent="onTouchMove">
          <button
            v-for="slot in timelineSlots"
            :key="'slot_' + slot.minutes"
            class="button is-small slot"
            :class="slotClass(slot)"
            :data-minutes="slot.minutes"
            :disabled="!slot.available"
            @mousedown.prevent="startDrag(slot)"
            @mouseenter="onDragOver(slot.minutes)"
            @touchstart.prevent="startDrag(slot)"
          >
            {{ slot.minutes | minutesToTime }}
          </button>
        </div>

        <div class="slot-legend is-size-7 has-text-grey mt-2">
          <span>
            <span class="legend-swatch swatch-available"></span>
            {{
              $t(
                'Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Legend_Available'
              )
            }}
          </span>
          <span>
            <span class="legend-swatch swatch-booked"></span>
            {{
              $t(
                'Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Legend_Booked'
              )
            }}
          </span>
          <span>
            <span class="legend-swatch swatch-closed"></span>
            {{
              $t(
                'Components.FrontEnd.BookSpaceDirect.TimeSlotPicker.Legend_Closed'
              )
            }}
          </span>
        </div>

        <div v-if="hasTimeSelection" class="mt-3">
          <span class="icon is-small mr-1">
            <font-awesome-icon :icon="['fas', 'clock']" />
          </span>
          <span class="has-text-weight-bold">
            {{ startMinutes | minutesToTime }} -
            {{ endMinutes | minutesToTime }}
          </span>
          <span class="has-text-grey">
            ({{ (endMinutes - startMinutes) | minutesToDuration }})
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const SLOT_INTERVAL = 15
const DEFAULT_DURATION = 60

export default {
  name: 'TimeSlotPicker',

  components: {
    Message: () => import('@/components/UI/Message'),
  },

  props: {
    daySchedule: {
      type: Object,
      default: null,
    },
    reservedItems: {
      type: Array,
      default: () => [],
    },
    isToday: {
      type: Boolean,
      default: false,
    },
    startMinutes: {
      type: Number,
      default: null,
    },
    endMinutes: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      dragAnchorMinutes: null,
      dragCurrentMinutes: null,
      isDragging: false,
    }
  },

  computed: {
    isClosed() {
      return (
        !this.daySchedule ||
        this.daySchedule.NrOfLocations === 0 ||
        this.daySchedule.MaxMinutes <= this.daySchedule.MinMinutes
      )
    },

    /**
     * The earliest minutes a booking may start at. For today this is
     * the current time rounded to the nearest quarter of an hour.
     */
    earliestStartMinutes() {
      if (!this.daySchedule) return 0

      let minStart = this.daySchedule.MinMinutes
      if (this.isToday) {
        let now = new Date()
        let nowMinutes = now.getHours() * 60 + now.getMinutes()
        minStart = Math.max(
          minStart,
          Math.round(nowMinutes / SLOT_INTERVAL) * SLOT_INTERVAL
        )
      }

      return minStart
    },

    /**
     * All quarter-hour blocks shown on the timeline. The hour before
     * opening and after closing is rendered (disabled) so the booker
     * can see the boundaries of the opening hours.
     */
    timelineSlots() {
      if (this.isClosed) return []

      let open = this.daySchedule.MinMinutes
      let close = this.daySchedule.MaxMinutes
      let renderStart = Math.max(0, open - 60)
      let renderEnd = Math.min(24 * 60, close + 60)

      let slots = []
      for (
        let m = renderStart;
        m + SLOT_INTERVAL <= renderEnd;
        m += SLOT_INTERVAL
      ) {
        let withinOpeningHours = m >= open && m + SLOT_INTERVAL <= close
        let booked = withinOpeningHours && this.isBlockBooked(m)

        slots.push({
          minutes: m,
          withinOpeningHours,
          booked,
          available:
            withinOpeningHours && !booked && m >= this.earliestStartMinutes,
        })
      }

      return slots
    },

    availableSlotSet() {
      return new Set(
        this.timelineSlots.filter((s) => s.available).map((s) => s.minutes)
      )
    },

    hasAvailableSlots() {
      return this.availableSlotSet.size > 0
    },

    /**
     * The range being dragged, as { start, end } in minutes (end exclusive)
     */
    previewRange() {
      if (!this.isDragging || this.dragAnchorMinutes === null) return null

      return {
        start: Math.min(this.dragAnchorMinutes, this.dragCurrentMinutes),
        end:
          Math.max(this.dragAnchorMinutes, this.dragCurrentMinutes) +
          SLOT_INTERVAL,
      }
    },

    hasTimeSelection() {
      return (
        this.startMinutes !== null &&
        this.endMinutes !== null &&
        this.endMinutes > this.startMinutes
      )
    },
  },

  created() {
    this.setDefaultSelection()
  },

  beforeDestroy() {
    document.removeEventListener('mouseup', this.endDrag)
    document.removeEventListener('touchend', this.endDrag)
  },

  methods: {
    /**
     * Pre-select the first available time slot. For today this is the
     * current time rounded to the nearest 15 minutes.
     */
    setDefaultSelection() {
      let firstSlot = this.timelineSlots.find((s) => s.available)
      if (firstSlot) {
        this.applySelection(
          firstSlot.minutes,
          firstSlot.minutes + DEFAULT_DURATION
        )
      }
    },

    /**
     * Apply a start/end selection, clamped to the contiguous free
     * region around the start so a selection never overlaps a
     * reservation or runs past closing time.
     */
    applySelection(startMinutes, endMinutes) {
      let region = this.getFreeRegion(startMinutes)
      if (!region) return

      this.$emit('select', {
        startMinutes: Math.max(startMinutes, region.start),
        endMinutes: Math.min(
          Math.max(endMinutes, startMinutes + SLOT_INTERVAL),
          region.end
        ),
      })
    },

    /**
     * The contiguous run of free quarters around the given slot,
     * as { start, end } in minutes (end exclusive)
     */
    getFreeRegion(minutes) {
      if (!this.availableSlotSet.has(minutes)) return null

      let start = minutes
      while (this.availableSlotSet.has(start - SLOT_INTERVAL)) {
        start -= SLOT_INTERVAL
      }

      let last = minutes
      while (this.availableSlotSet.has(last + SLOT_INTERVAL)) {
        last += SLOT_INTERVAL
      }

      return { start, end: last + SLOT_INTERVAL }
    },

    slotClass(slot) {
      let preview = this.previewRange
      let inPreview =
        preview && slot.minutes >= preview.start && slot.minutes < preview.end
      let inSelection =
        !this.isDragging &&
        this.hasTimeSelection &&
        slot.minutes >= this.startMinutes &&
        slot.minutes < this.endMinutes

      return {
        'is-primary': inPreview || inSelection,
        'is-booked': slot.booked,
        'is-closed': !slot.withinOpeningHours,
      }
    },

    startDrag(slot) {
      if (!slot.available) return

      this.isDragging = true
      this.dragAnchorMinutes = slot.minutes
      this.dragCurrentMinutes = slot.minutes

      document.addEventListener('mouseup', this.endDrag)
      document.addEventListener('touchend', this.endDrag)
    },

    onDragOver(minutes) {
      if (!this.isDragging) return

      // Clamp to the contiguous free region around the anchor so the
      // drag can never cross a reservation or the opening hours
      let region = this.getFreeRegion(this.dragAnchorMinutes)
      if (!region) return

      this.dragCurrentMinutes = Math.min(
        Math.max(minutes, region.start),
        region.end - SLOT_INTERVAL
      )
    },

    onTouchMove(event) {
      if (!this.isDragging || !event.touches.length) return

      let touch = event.touches[0]
      let element = document.elementFromPoint(touch.clientX, touch.clientY)
      let slotElement = element ? element.closest('[data-minutes]') : null
      if (slotElement) {
        this.onDragOver(Number(slotElement.dataset.minutes))
      }
    },

    endDrag() {
      document.removeEventListener('mouseup', this.endDrag)
      document.removeEventListener('touchend', this.endDrag)

      if (!this.isDragging) return
      this.isDragging = false

      let anchor = this.dragAnchorMinutes
      let current = this.dragCurrentMinutes
      this.dragAnchorMinutes = null
      this.dragCurrentMinutes = null

      if (anchor === null || current === null) return

      if (anchor === current) {
        // Plain click: select the default duration from this slot
        this.applySelection(anchor, anchor + DEFAULT_DURATION)
      } else {
        this.applySelection(
          Math.min(anchor, current),
          Math.max(anchor, current) + SLOT_INTERVAL
        )
      }
    },

    /**
     * Is the 15 minute block starting at the given minutes already booked?
     */
    isBlockBooked(minutes) {
      return this.reservedItems.some(
        (i) => minutes < i.EndMinutes && minutes + SLOT_INTERVAL > i.StartMinutes
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(64px, 1fr));
  gap: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
  user-select: none;

  .slot {
    touch-action: none;
  }

  .button:disabled {
    opacity: 0.6;
  }

  .button.is-closed:disabled {
    background-color: $grey-lighter;
    border-color: transparent;
    color: $grey;
  }

  .button.is-booked:disabled {
    background-color: $danger-light;
    border-color: transparent;
    color: $danger-dark;
    text-decoration: line-through;
  }
}

.slot-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  .legend-swatch {
    display: inline-block;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 2px;
    vertical-align: middle;

    &.swatch-available {
      background-color: $white;
      border: 1px solid $grey-light;
    }

    &.swatch-booked {
      background-color: $danger-light;
    }

    &.swatch-closed {
      background-color: $grey-lighter;
    }
  }
}
</style>
